import { useQuery } from '@tanstack/react-query'
import { compact, sortBy, uniqBy } from 'lodash'
import moment from 'moment/moment'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { usePageTitle } from '../../hooks/usePageTitle'
import { ComputeFactory } from '../../models/ComputeFactory'
import { Message } from '../../models/Message'
import { RecordFactory } from '../../models/RecordFactory'
import { Team } from '../../models/Team'
import { User } from '../../models/User'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { PERM_ROLE_ADMIN } from '../../utils'
import { DATE_FORMAT, SHORT_DOW_DATE_FORMAT, TIME_FORMAT } from '../../utils/dates'
import { getIconNameFromType } from '../../utils/item'
import { currencyFormat } from '../../utils/numbers'
import { ComponentData } from '../../utils/typing'
import { Icon } from '../Icon'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import { PSApplication } from '../app/PSApplication'
import Avatar from '../app/design/Avatar'
import Card from '../app/design/Card'
import Divider from '../app/design/Divider'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { ImageView } from '../common/ImageView'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { HeaderButton, BaseRecordView } from '../common/sections'
import { ContactAvatar } from '../contacts/ContactAvatar'
import { WorkflowPage } from '../workflows/WorkflowPage'

const viewWillAppear = async ({ urlParams }: PocketComponent) => {
    const reservationID = urlParams.id
    if (!reservationID) {
        return new RecordNotFoundError('Reservation not found')
    }

    const result = await RecordFactory.getInstance().fetchItem(reservationID, 'reservation')

    if (!result.ok) {
        return new RecordNotFoundError('Reservation not found')
    }

    const reservation = !result.value
        ? await RecordFactory.getInstance().loadItem(reservationID)
        : result.value

    if (!reservation) {
        return new RecordNotFoundError('Reservation not found')
    }

    return { reservation }
}

const render = ({
    data,
    base: { logAndDisplayError, me },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const { reservation } = data

    const navigate = useNavigate()

    usePageTitle(reservation.name)

    const { findLastHistoryEntry } = useHistoryTrackerContext()

    const backHistoryEntry = findLastHistoryEntry({
        excluding: /^\/settings\/reservations.*\/(new|edit)/,
    })

    const { data: capacityStatus } = useQuery({
        queryKey: ['reservation', 'capacityStatus', reservation.id],
        queryFn: () => reservation.capacityStatus(),
    })

    const { data: bookings = [] } = useQuery({
        queryKey: ['reservation', 'bookings', reservation.id],
        queryFn: () => ComputeFactory.getInstance().fetchItemBookings(reservation, false),
    })

    const { data: team } = useQuery({
        queryKey: ['reservation', 'team', reservation.id, me],
        queryFn: async () => {
            const confirmedBookings = await ComputeFactory.getInstance().fetchItemBookings(
                reservation,
                true
            )

            const clients = compact(confirmedBookings.map((booking) => booking.client))

            return clients.length > 0
                ? ComputeFactory.getInstance().fetchTeamByPeople(
                      uniqBy([me, ...clients], (user) => user.id ?? '')
                  )
                : null
        },
    })

    const [isShowingCancelConfirmation, setIsShowingCancelConfirmation] = useState(false)

    const handleCancel = async (notifyAll: boolean) => {
        setIsShowingCancelConfirmation(false)

        if (!reservation.id) return

        const result = await RecordFactory.getInstance().deleteItem(reservation.id, notifyAll)

        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        navigate(backHistoryEntry?.pathname ?? '/calendar')
    }

    const handleMessage = async (thread: User | Team) => {
        // If the user is external to the recipient, and is trying to talk with an admin that has shared inbox on,
        // open thread with employer instead
        if (
            thread instanceof User &&
            thread.payee &&
            thread.perm === PERM_ROLE_ADMIN &&
            !!thread.employer &&
            thread.inbox === thread.employer.id &&
            PSApplication.owner().id !== thread.employer.id
        ) {
            thread = thread.employer
        }

        const lastSyncDate = thread.id ? await Message.lastSyncDate(thread.id) : undefined

        if (
            thread.id &&
            (!lastSyncDate ||
                (await ComputeFactory.getInstance().hasMessageSinceLastModifiedDate(
                    lastSyncDate,
                    thread
                )))
        ) {
            // TODO: maybe this type of behavior is better suited in the MessagesPage loader?
            const result = await RecordFactory.getInstance().fetchThread(thread)

            if (!result.ok) {
                logAndDisplayError(result.error)
                return
            }
        }

        navigate(`/messages/${thread.id}`, { state: { bcc: true } })
    }

    const headerButtons = (() => {
        const buttons: HeaderButton[] = []

        if (
            !reservation.cancelledDate &&
            !!bookings &&
            bookings.length < (reservation.capacity ?? 0)
        ) {
            buttons.push({
                title: 'Schedule',
                onClick: () =>
                    navigate(
                        `/bookings/new?reservationId=${encodeURIComponent(reservation.id ?? '')}`
                    ),
            })
        }

        if (!reservation.cancelledDate && !!team) {
            buttons.push({
                title: 'Cancel',
                onClick: () => setIsShowingCancelConfirmation(true),
            })
        }

        // TODO: Messages don't currently handle being passed a thread (i.e. team) without an id;
        // so we hide the button unless the thread already exists (same issue exists in the LessonRecord)
        if (!!team?.id) {
            buttons.push({ title: 'Message', onClick: () => handleMessage(team) })
        }

        buttons.push({
            title: 'Edit',
            onClick: () => navigate(`/settings/reservations/${reservation.id}/edit`),
        })

        return buttons
    })()

    return (
        <BaseRecordView
            title="Reservation"
            headerButtons={headerButtons}
            backButton={
                backHistoryEntry !== undefined
                    ? {
                          title: backHistoryEntry.title ?? 'Back',
                          onClick: backHistoryEntry.goBackTo,
                      }
                    : {
                          title: 'Calendar',
                          onClick: () => navigate('/calendar'),
                      }
            }
        >
            <BaseRecordView.Container>
                <BaseRecordView.LeftColumn>
                    <Card size="sm">
                        <div className="flex flex-col items-center justify-center gap-6">
                            {!!reservation?.iconConfig && (
                                <Avatar size="xxl">
                                    <ImageView
                                        fallbackIcon={{
                                            icon: `${getIconNameFromType('reservation')}.large`,
                                            background: 'round',
                                            className: 'size-[116px]',
                                            svgClassName: 'size-[56px]',
                                        }}
                                        className="size-full rounded-full object-cover"
                                        image={reservation.image}
                                    />
                                </Avatar>
                            )}

                            <div className="flex flex-col items-center justify-center gap-y-1">
                                <Heading size="sm" className="text-center">
                                    {reservation.name ?? ''}
                                </Heading>
                                {reservation.date && (
                                    <Paragraph size="lg" className="font-normal text-gray-600">
                                        {reservation.date.format(SHORT_DOW_DATE_FORMAT)}
                                    </Paragraph>
                                )}
                                {reservation.endDate && (
                                    <Paragraph size="lg" className="font-normal text-gray-600">
                                        Check out {reservation.endDate.format('h:mm A, MMM Do')}
                                    </Paragraph>
                                )}
                            </div>
                        </div>

                        <Divider className="my-6" />

                        <div className="flex flex-col items-stretch justify-start gap-y-8">
                            <BaseRecordView.IconField
                                icon="users.large"
                                title={capacityStatus ?? ''}
                                subtitle="Class capacity"
                            />

                            <BaseRecordView.IconField
                                icon="dollar.large"
                                title={
                                    reservation.rate === undefined
                                        ? 'TBD'
                                        : reservation.rate === 0
                                          ? 'Free'
                                          : currencyFormat(reservation.rate)
                                }
                                subtitle="Price"
                            />
                        </div>
                    </Card>

                    {!!reservation.location &&
                        !!reservation.latitude &&
                        !!reservation.longitude && (
                            <BaseRecordView.LocationSection
                                title="Location"
                                location={reservation.location}
                                latLong={{
                                    latitude: reservation.latitude,
                                    longitude: reservation.longitude,
                                }}
                            />
                        )}
                </BaseRecordView.LeftColumn>

                <BaseRecordView.RightColumn>
                    {bookings.length > 0 && (
                        <Card
                            className="flex flex-col items-stretch justify-start gap-y-8"
                            title="Roster"
                            count={bookings.length > 0 ? bookings.length : undefined}
                        >
                            <div className="flex flex-col items-stretch justify-center gap-y-8">
                                {sortBy(
                                    bookings,
                                    (booking) => booking.client?.name?.toLowerCase() ?? ''
                                ).map((booking) => (
                                    <Link
                                        key={booking.id ?? ''}
                                        to={`/bookings/${booking.id}`}
                                        className="group relative"
                                    >
                                        <div className="absolute -inset-4 z-10 rounded-lg group-hover:bg-black/4" />

                                        <div className="relative z-20 flex items-center justify-center gap-x-4">
                                            {booking.client !== undefined ? (
                                                <ContactAvatar
                                                    user={booking.client}
                                                    hasHoverEffect
                                                />
                                            ) : (
                                                <Icon icon="user.large" />
                                            )}

                                            <div className="flex grow flex-col items-stretch justify-center gap-y-1">
                                                <Label size="md" className="font-bold">
                                                    {booking.client?.name}
                                                </Label>
                                                <Paragraph size="sm" className="text-gray-600">
                                                    {booking.checkinDate !== undefined
                                                        ? `Checked in at ${moment(booking.checkinDate).format(TIME_FORMAT)} on ${moment(booking.checkinDate).format(DATE_FORMAT)}` +
                                                          (booking.checkinBy !== undefined
                                                              ? ` by ${booking.checkinBy.name}`
                                                              : '')
                                                        : 'Client'}
                                                </Paragraph>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </Card>
                    )}

                    <Card>
                        <Heading size="sm" className="text-gray-700">
                            Description
                        </Heading>

                        <Paragraph size="md" className="mt-8 text-gray-600">
                            {reservation.memo}
                        </Paragraph>
                    </Card>

                    <WorkflowPage.FormAndContractCard
                        form={reservation.form}
                        contract={reservation.contract}
                    />
                </BaseRecordView.RightColumn>
            </BaseRecordView.Container>

            {isShowingCancelConfirmation && (
                <ConfirmationModal
                    isOpen
                    title="Cancel reservation?"
                    cancelButtonText="Cancel reservation"
                    confirmButtonText="Cancel & Notify"
                    onClose={() => setIsShowingCancelConfirmation(false)}
                    onConfirm={() => handleCancel(true)}
                    onCancel={() => handleCancel(false)}
                    isDestructive
                >
                    Are you sure you want to cancel this reservation?
                </ConfirmationModal>
            )}
        </BaseRecordView>
    )
}

export const FixedReservationPage = usePSComponent(render, viewWillAppear)
